import React, { useState, useCallback, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Plus, 
  HelpCircle, 
  Settings, 
  ShieldCheck,
  ChevronLeft,
  ChevronRight,
  LogOut,
  CreditCard,
  Users,
  Menu,
  X,
  Lightbulb
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const ADMIN_EMAIL = 'pgundepudi@gmail.com';

/**
 * SaaS-style collapsible sidebar layout
 */
const SidebarLayout = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  
  const isAdmin = user?.email === ADMIN_EMAIL;

  const menuItems = useMemo(() => [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard', show: true },
    { icon: Plus, label: 'New Project', path: '/briefing', show: true },
    { icon: CreditCard, label: 'Credits', path: '/credits', show: true },
    { icon: Users, label: 'Team', path: '/team', show: true },
    { icon: HelpCircle, label: 'Support', path: '/support', show: true },
    { icon: Lightbulb, label: 'Feature Requests', path: '/feature-requests', show: true },
    { icon: Settings, label: 'Settings', path: '/settings', show: true },
    { icon: ShieldCheck, label: 'Admin Panel', path: '/admin/projects', show: isAdmin },
    { icon: HelpCircle, label: 'Support Center', path: '/admin/support', show: isAdmin },
  ], [isAdmin]);

  const handleLogout = useCallback(() => {
    logout();
    navigate('/');
  }, [logout, navigate]);

  const closeMobileMenu = useCallback(() => {
    setMobileMenuOpen(false);
  }, []);

  const toggleMobileMenu = useCallback(() => {
    setMobileMenuOpen(prev => !prev);
  }, []);

  const toggleCollapsed = useCallback(() => {
    setCollapsed(prev => !prev);
  }, []);

  const isActive = useCallback((path) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard' || location.pathname.startsWith('/project/');
    }
    return location.pathname.startsWith(path);
  }, [location.pathname]);

  // Render menu item
  const renderMenuItem = (item, isCollapsedView = false) => {
    const Icon = item.icon;
    const active = isActive(item.path);
    
    return (
      <Link
        key={item.path}
        to={item.path}
        onClick={closeMobileMenu}
        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
          active 
            ? 'bg-gradient-to-r from-amber-500/20 to-yellow-500/20 text-amber-400 border-l-2 border-amber-400' 
            : 'text-slate-400 hover:bg-slate-700/50 hover:text-white'
        } ${isCollapsedView ? 'justify-center' : ''}`}
        title={isCollapsedView ? item.label : ''}
      >
        <Icon className={`w-5 h-5 flex-shrink-0 ${active ? 'text-amber-400' : ''}`} />
        {!isCollapsedView && <span className="text-sm font-medium">{item.label}</span>}
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-slate-900 flex">
      {/* Desktop Sidebar */}
      <aside 
        className={`hidden md:flex flex-col bg-slate-800 border-r border-slate-700 transition-all duration-300 relative ${
          collapsed ? 'w-16' : 'w-64'
        }`}
      >
        {/* Logo / App Name */}
        <div className={`px-4 py-4 border-b border-slate-700 ${collapsed ? 'text-center' : ''}`}>
          <Link to="/dashboard" className="flex items-center gap-3">
            {/* Wow!CX Logo - Small version for sidebar */}
            <img 
              src="/wowcx-logo.png?v=2"
              alt="Wow!CX"
              className="h-7 w-auto object-contain flex-shrink-0"
            />
            {!collapsed && (
              <div className="flex flex-col">
                <div className="flex items-center gap-1.5">
                  <span className="text-white font-bold text-sm leading-tight">Design Studio</span>
                  <span className="px-1.5 py-0.5 bg-amber-500/20 text-amber-400 text-[10px] font-bold rounded uppercase">Beta</span>
                </div>
              </div>
            )}
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
          {menuItems.filter(item => item.show).map(item => renderMenuItem(item, collapsed))}
        </nav>

        {/* User Section */}
        <div className="border-t border-slate-700 p-3">
          {!collapsed && user && (
            <div className="flex items-center gap-3 px-2 py-2 mb-2">
              {user.picture ? (
                <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-slate-600 flex items-center justify-center">
                  <span className="text-white text-sm font-medium">{user.name?.charAt(0) || 'U'}</span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">{user.name}</p>
                <p className="text-slate-400 text-xs truncate">{user.email}</p>
              </div>
            </div>
          )}
          <button
            onClick={handleLogout}
            className={`flex items-center gap-3 px-3 py-2 w-full rounded-lg text-slate-400 hover:bg-red-500/10 hover:text-red-400 transition-all ${collapsed ? 'justify-center' : ''}`}
            title={collapsed ? 'Sign Out' : ''}
          >
            <LogOut className="w-5 h-5" />
            {!collapsed && <span className="text-sm font-medium">Sign Out</span>}
          </button>
        </div>

        {/* Collapse Toggle */}
        <button
          onClick={toggleCollapsed}
          className="absolute -right-3 top-20 w-6 h-6 bg-slate-700 border border-slate-600 rounded-full flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-600 transition-all shadow-lg"
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>
      </aside>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 h-14 bg-slate-800 border-b border-slate-700 flex items-center justify-between px-4 z-50">
        <Link to="/dashboard" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center">
            <span className="text-white font-bold text-sm">W</span>
          </div>
          <span className="text-white font-bold text-sm">Wow!CX Design Studio</span>
        </Link>
        <button
          onClick={toggleMobileMenu}
          className="p-2 text-slate-400 hover:text-white"
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black/50 z-40"
          onClick={closeMobileMenu}
        />
      )}

      {/* Mobile Sidebar */}
      <aside 
        className={`md:hidden fixed top-14 left-0 bottom-0 w-64 bg-slate-800 border-r border-slate-700 z-50 transform transition-transform duration-300 flex flex-col ${
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <nav className="flex-1 px-2 py-4 space-y-1">
          {menuItems.filter(item => item.show).map(item => renderMenuItem(item, false))}
        </nav>
        <div className="border-t border-slate-700 p-3">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-3 py-2 w-full rounded-lg text-slate-400 hover:bg-red-500/10 hover:text-red-400 transition-all"
          >
            <LogOut className="w-5 h-5" />
            <span className="text-sm font-medium">Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-h-screen md:min-h-0">
        {/* Mobile spacer */}
        <div className="h-14 md:hidden" />
        
        {/* Content */}
        <div className="flex-1 overflow-auto">
          {children}
        </div>

        {/* Footer */}
        <footer className="bg-slate-800 border-t border-slate-700 py-3 px-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-slate-500">
            <span>© 2025 Wow!CX. All rights reserved.</span>
            <div className="flex items-center gap-4">
              <a 
                href="https://wowcx.io/terms-of-use" 
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:text-slate-300 transition-colors"
              >
                Terms of Use
              </a>
              <a 
                href="https://wowcx.io/privacy-policy" 
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:text-slate-300 transition-colors"
              >
                Privacy Policy
              </a>
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default SidebarLayout;
